import { useCallback, useMemo, type ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { AppList } from '@/shared/app-list-layout';
import type { DataTableView } from '@/shared/app-list-layout';
import type { ListColumn } from '@/shared/app-list-layout/types';
import { InventoryApi } from '@/modules/inventory/services/api';
import type { StockBalance } from '@/api/schemas/inventory';

/**
 * Stock balances.
 *
 * The rows come from M18's balance read; the sku label is a best-effort join
 * onto the tenant's offers (see the module service for why it degrades).
 *
 * A quantity is a DECIMAL STRING on the wire and is rendered as such. It is
 * never parsed into a float: the API keeps quantities in `numeric` precisely so
 * a fractional quantity survives, and a number would quietly round it.
 */

/** The label a row is known by: the sku when the actor may read it, else the item id. */
function labelFor(balance: StockBalance, skus: Map<string, string>): string {
  return skus.get(balance.itemId) ?? balance.itemId;
}

/**
 * Saved views rendered as tabs above the stock list.
 *
 * The views are derived from the balance itself rather than from a status field,
 * because a balance has no status: what an operator wants to see first is the
 * stock that is actually sellable, and then the stock that is held but not free.
 */
function buildStockViews(counts: Partial<Record<string, number>>): DataTableView[] {
  return [
    { id: 'all', label: 'All stock', count: counts.all },
    { id: 'available', label: 'Available', filters: { availability: 'available' }, count: counts.available },
    { id: 'reserved', label: 'Reserved', filters: { availability: 'reserved' }, count: counts.reserved },
  ];
}

/**
 * The fields a search term is matched against.
 *
 * The sku is not among them: it is joined in from a DIFFERENT read that may have
 * been refused, so matching on it would make the search box find a row only for
 * the actors who can read offers. The item id is what every actor can see.
 */
function searchFields(row: StockBalance): ReadonlyArray<string | null | undefined> {
  return [row.itemId, row.unitOfMeasure];
}

const COLUMNS: ReadonlyArray<ListColumn<StockBalance>> = [
  {
    id: 'item',
    header: 'Item',
    primary: true,
    sortable: true,
    cell: (row) => <span className="font-medium text-content">{row.id.slice(0, 8)}</span>,
  },
  {
    id: 'onHand',
    header: 'On hand',
    cell: (row) => <span>{row.onHand}</span>,
  },
  {
    id: 'reserved',
    header: 'Reserved',
    cell: (row) => <span>{row.reserved}</span>,
  },
  {
    id: 'available',
    header: 'Available',
    cell: (row) => <span className="font-medium text-content">{row.available}</span>,
  },
  {
    id: 'unitOfMeasure',
    header: 'Unit',
    sortable: true,
    cell: (row) => <span className="text-content-muted">{row.unitOfMeasure}</span>,
  },
  {
    id: 'updatedAt',
    header: 'Updated',
    sortable: true,
    cell: (row) => (
      <span className="whitespace-nowrap">
        {new Date(row.updatedAt).toLocaleDateString()}
        <span className="ml-1.5 text-content-muted">
          {new Date(row.updatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </span>
      </span>
    ),
  },
];

export function ListPage(): ReactNode {
  const navigate = useNavigate();
  const read = useCallback(async () => {
    const [balances, skus] = await Promise.all([
      InventoryApi.listBalances(),
      InventoryApi.resolveSkus(),
    ]);
    return balances.map((balance) => ({ ...balance, sku: labelFor(balance, skus) }));
  }, []);

  const columns = useMemo(
    (): ReadonlyArray<ListColumn<StockBalance & { sku: string }>> =>
      COLUMNS.map((column) =>
        column.id === 'item'
          ? {
              ...column,
              cell: (row) => <span className="font-medium text-content">{row.sku}</span>,
            }
          : column,
      ),
    [],
  );

  return (
    <AppList<StockBalance & { sku: string }>
      title="Stock"
      description="What this tenant holds, what is reserved, and what is free to sell."
      read={read}
      searchFields={searchFields}
      countKeyOf={(row) => (Number(row.available) > 0 ? 'available' : 'reserved')}
      buildViews={buildStockViews}
      columns={columns}
      getRowId={(row) => row.id}
      rowActions={[
        {
          id: 'movements',
          label: 'Movements',
          onSelect: (row) => {
            void navigate({ to: '/inventory/stock/$itemId', params: { itemId: row.itemId } });
          },
        },
      ]}
      // Six columns run off a phone screen, so the page asks for the responsive
      // presentation: cards below `sm`, the table above.
      responsive
      emptyTitle="No stock recorded"
      emptyDescription="This tenant has not recorded a stock balance yet."
      noMatchDescription="No balance matches this tab, search or filter."
    />
  );
}
